/**
 * Símbolos dos TIPOS DE ATIVIDADE do CRM — vocabulário único (LEAD-TELA, 05/08).
 *
 * A coluna `tipos_atividade.icone` sempre existiu semeada em slug minúsculo
 * (`phone`, `mail`, `message`, `map-pin`, `note`) e NUNCA foi renderizada —
 * nenhum componente a consumia. Este registro fecha o circuito: os 5 slugs
 * seedados continuam válidos e ganham os 9 companheiros do mockup aprovado
 * (docs/architecture/mockup-lead-comercial.html — grade de 14, "poucos e
 * bons").
 *
 * Regras do mockup, que são regras daqui:
 *  - ZERO emoji: todo símbolo é SVG do lucide (mesma família do app inteiro,
 *    traço, 24×24, cor herdada). A régua \p{Extended_Pictographic} do teste
 *    de catálogo vale para os títulos desta grade.
 *  - Sem escolha, o tipo nasce com o símbolo de NOTA (neutro) — é também o
 *    fallback de qualquer slug desconhecido, então dado velho nunca quebra.
 *  - O registro é FECHADO (14): catálogo de ícones vira ruído; se um dia
 *    faltar símbolo, a grade cresce aqui e nos testes, num lugar só.
 *
 * Não confundir com `ICON_MAP`/`IconByName` (PascalCase, motor antigo
 * `activity_types` do R5.2) — vocabulário e tabela DIFERENTES.
 */
import {
  Bell,
  Briefcase,
  Calendar,
  Clock,
  Edit3,
  FileText,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  StickyNote,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/cn';

/** Slug persistido em `tipos_atividade.icone` → símbolo + título humano. */
// eslint-disable-next-line react-refresh/only-export-components -- catálogo fechado vive junto do render, de propósito (um vocabulário, um arquivo)
export const SIMBOLOS_ATIVIDADE: ReadonlyArray<{ slug: string; titulo: string; Icone: LucideIcon }> = [
  { slug: 'phone', titulo: 'Ligação', Icone: Phone },
  { slug: 'mail', titulo: 'E-mail', Icone: Mail },
  { slug: 'message', titulo: 'Mensagem', Icone: MessageCircle },
  { slug: 'map-pin', titulo: 'Visita', Icone: MapPin },
  { slug: 'documento', titulo: 'Documento', Icone: FileText },
  { slug: 'lapis', titulo: 'Revisão', Icone: Edit3 },
  { slug: 'note', titulo: 'Nota', Icone: StickyNote },
  { slug: 'agenda', titulo: 'Agenda', Icone: Calendar },
  { slug: 'prazo', titulo: 'Prazo', Icone: Clock },
  { slug: 'reuniao', titulo: 'Reunião', Icone: Users },
  { slug: 'video', titulo: 'Vídeo', Icone: Video },
  { slug: 'negocio', titulo: 'Negócio', Icone: Briefcase },
  { slug: 'aviso', titulo: 'Aviso', Icone: Bell },
  { slug: 'envio', titulo: 'Envio', Icone: Send },
];

/** O padrão de quem não escolher — e o fallback de slug desconhecido. */
export const SIMBOLO_PADRAO = 'note';

function resolve(slug: string | null | undefined): LucideIcon {
  const hit = SIMBOLOS_ATIVIDADE.find((s) => s.slug === slug);
  return (hit ?? SIMBOLOS_ATIVIDADE.find((s) => s.slug === SIMBOLO_PADRAO)!).Icone;
}

/**
 * O símbolo de um tipo de atividade, por slug. Sempre renderiza algo — slug
 * nulo/desconhecido cai no símbolo de nota. `aria-hidden`: o nome do tipo é
 * quem fala; o símbolo acompanha.
 */
export function SimboloAtividade(props: { icone: string | null | undefined; className?: string }) {
  const Icone = resolve(props.icone);
  return <Icone aria-hidden="true" className={cn('w-4 h-4', props.className)} />;
}

/**
 * A grade de escolha (14 opções) — usada no cadastro do tipo em
 * Configurações › Catálogos › Tipos de atividade. Botões reais com título
 * acessível; o selecionado fica marcado com `aria-pressed`.
 */
export function GradeSimbolos(props: {
  valor: string;
  onEscolher: (slug: string) => void;
  testId?: string;
}) {
  return (
    <div data-testid={props.testId ?? 'grade-simbolos'} className="grid grid-cols-7 gap-1.5">
      {SIMBOLOS_ATIVIDADE.map(({ slug, titulo, Icone }) => {
        const on = props.valor === slug;
        return (
          <button
            key={slug}
            type="button"
            title={titulo}
            aria-label={titulo}
            aria-pressed={on}
            onClick={() => props.onEscolher(slug)}
            className={cn(
              'aspect-square rounded-lg border inline-flex items-center justify-center',
              on
                ? 'bg-red border-red text-white'
                : 'border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface text-ink-2 dark:text-ink-dark-2 hover:border-red hover:text-red-deep dark:hover:text-red-glow',
            )}
          >
            <Icone aria-hidden="true" className="w-[17px] h-[17px]" />
          </button>
        );
      })}
    </div>
  );
}
